import { useEffect, useRef } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { STORAGE_KEY } from "./config";
import { detectBrowserLang, langFromPath, langFromQuery, readStoredLang, resolveLanguage } from "./detect";
import { pathForLocale } from "@/lib/i18n-paths";

/** Route IDs (English tree) that have a `/fi` mirror. */
const MIRRORED_ROUTE_IDS = new Set<string>([
  "/",
  "/eat",
  "/do",
  "/cities",
  "/happening/",
  "/happening/$slug",
  "/place/$id",
  "/$citySlug",
]);

/** Set once we've made (or deliberately skipped) the automatic choice. */
const DECIDED_KEY = "what.lang.autodetected";

/**
 * One-time automatic locale routing.
 *
 * On the very first visit we resolve the language with the documented fallback
 * order (URL → ?lang → stored → browser → English). If the result is Finnish
 * and the current page has a Finnish mirror, we replace-navigate into the
 * `/fi` tree so city pages and everything else render in the right language.
 * Runs client-side only, never fights an explicit URL, and never repeats — the
 * language switcher stays fully in control afterwards.
 */
export function AutoLocaleRedirect() {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const routeId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.routeId as string | undefined,
  });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let decided = false;
    try {
      decided = window.localStorage.getItem(DECIDED_KEY) === "1";
    } catch { /* private mode — treat as undecided, effect runs once per load */ }
    const markDecided = () => {
      try { window.localStorage.setItem(DECIDED_KEY, "1"); } catch { /* noop */ }
    };

    // Explicit signals: the URL is authoritative, remember it and stop.
    const explicit = langFromPath(pathname) ?? langFromQuery(searchStr);
    if (explicit) {
      try { window.localStorage.setItem(STORAGE_KEY, explicit); } catch { /* noop */ }
      markDecided();
      return;
    }
    if (decided) return;

    const stored = readStoredLang(STORAGE_KEY);
    const { lang, source } = resolveLanguage({
      pathname,
      search: searchStr,
      stored,
      browser: detectBrowserLang(),
    });

    if (source === "browser") {
      try { window.localStorage.setItem(STORAGE_KEY, lang); } catch { /* noop */ }
    }
    markDecided();

    if (lang !== "fi") return;
    if (!routeId || !MIRRORED_ROUTE_IDS.has(routeId)) return;

    const target = pathForLocale(pathname, "fi");
    if (target === pathname) return;
    void router.navigate({
      to: target,
      search: (prev: Record<string, unknown>) => prev,
      replace: true,
    } as never);
  }, [pathname, searchStr, routeId, router]);

  return null;
}
